import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { RefreshCw, Fingerprint } from 'lucide-react';

const CLASSES = ['good', 'bad', 'unknown'];
const KINDS = ['ja3', 'ja4'];

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const classTone = (c) =>
  c === 'good'
    ? 'text-emerald-600'
    : c === 'bad'
    ? 'text-rose-600'
    : 'text-muted-foreground';

export default function TlsFingerprints() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classification, setClassification] = useState('all');
  const [kind, setKind] = useState('all');

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 300 };
      if (classification !== 'all') params.classification = classification;
      if (kind !== 'all') params.kind = kind;
      const res = await axios.get(API + '/tls-fingerprints', { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading TLS fingerprints:', err);
      toast.error(t('Error loading fingerprints'));
    } finally {
      setLoading(false);
    }
  }, [classification, kind, t]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const reclassify = async (row, value) => {
    try {
      await axios.patch(API + '/tls-fingerprints/' + row.id, { classification: value });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, classification: value } : r)));
      toast.success(t('Fingerprint updated'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('Failed to update fingerprint'));
    }
  };

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Anti-fraud')}
        title={t('TLS fingerprints')}
        subtitle={t('JA3 / JA4 fingerprint overrides — mark a fingerprint as good or bad to steer bot detection.')}
        actions={
          <Button variant="outline" onClick={fetchRows} disabled={loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        }
      />

      <div className="flex flex-wrap gap-3">
        <div className="w-44">
          <Select value={classification} onValueChange={setClassification}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All classifications')}</SelectItem>
              {CLASSES.map((c) => (<SelectItem key={c} value={c}>{t(c)}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-36">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All kinds')}</SelectItem>
              {KINDS.map((k) => (<SelectItem key={k} value={k}>{k.toUpperCase()}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="skeleton h-96 w-full"></div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty
              icon={Fingerprint}
              title={t('No fingerprint overrides yet')}
              subtitle={t('As traffic arrives, JA3/JA4 fingerprints appear here so you can mark known-good or known-bad clients.')}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Fingerprint')}</TableHead>
                  <TableHead>{t('Kind')}</TableHead>
                  <TableHead>{t('Label')}</TableHead>
                  <TableHead>{t('Hits')}</TableHead>
                  <TableHead>{t('Last seen')}</TableHead>
                  <TableHead>{t('Classification')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs max-w-[18rem] truncate" title={r.fingerprint}>{r.fingerprint}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{(r.kind || 'ja3').toUpperCase()}</Badge></TableCell>
                    <TableCell className="text-sm">{r.label || '—'}</TableCell>
                    <TableCell className="text-sm">{r.hit_count ?? 0}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtTime(r.last_seen)}</TableCell>
                    <TableCell>
                      <div className={'w-32 ' + classTone(r.classification)}>
                        <Select value={r.classification || 'unknown'} onValueChange={(v) => reclassify(r, v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CLASSES.map((c) => (<SelectItem key={c} value={c}>{t(c)}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
